import React, { Fragment, memo, useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import styles from './AddTeamMember.module.scss'
import CommonFormFieldWrapper, { getFormInputErrors } from '../../../../common/components/form-field-wrapper/CommonFormFieldWrapper'
import SelectedOptionTopBar from '../../../../common/components/selected-option-top-bar/SelectedOptionTopBar'
import { useNavigate } from 'react-router-dom'
import CommonSelect, { CommonSelectItem } from '../../../../common/components/select/CommonSelect'
import { toast } from 'react-toastify'
import CommonCheckBoxList from '../../../../common/components/checkboxList/CommonCheckBoxList'
import CommonPhoneInput from '../../../../common/components/phone-input/CommonPhoneInput'
import FormWrapper, { TitleSectionProps } from '../../../../common/components/form-wrapper/FormWrapper'
import { useGetAllPermissionsQuery, useInviteNewTeamMemberMutation } from '../../../../service/solar-provider-service'
import { getErrorMessage } from '../../../../utils/error-handler'

type Props = {}

type AddTeamMemberForm = {
    firstName: string,
    lastName: string,
    email: string,
    employeeType: string,
    phoneNumber: string,
    permissions: string[]
}

const AddTeamMember: React.FC<Props> = memo((props: Props) => {
    const [ permissions, setPermissions ] = useState<Array<{ name: string}>>([]);
    const navigate = useNavigate();

    const { 
        data: permissionsData, 
        error: permissionsError,
    } = useGetAllPermissionsQuery();
    
    const [
        inviteNewTeamMember, 
        { isLoading: isInviting }
    ] = useInviteNewTeamMemberMutation();

    const {
        register,
        setValue,
        getValues,
        handleSubmit,
        watch,
        formState: { errors, isValid },
    } = useForm<AddTeamMemberForm>({
        reValidateMode: 'onChange',
        mode: 'onTouched',
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            employeeType: '',
            phoneNumber: '',
            permissions: []
        }
    })

      useEffect(() => {
        if (permissionsData) {
            setPermissions(permissionsData.permissions);
        }
    }, [permissionsData]);

    useEffect(() => {
        if (permissionsError && 'data' in permissionsError) {
            toast.error(getErrorMessage(permissionsError) ?? "There was an error loading permissions");
        }
    }, [permissionsError]);


    const onBackFunction = () => navigate('/provider/team-members');

    const roles : CommonSelectItem[] = [
        new CommonSelectItem(2, 'Admin'),
        new CommonSelectItem(3, 'Sales'),
        new CommonSelectItem(4, 'Installer')
    ]

    const titleSection: TitleSectionProps = {
        title: 'Member Information',
        buttonTitle: '',
        subtitle: ''
    }

    const saveChanges = async (data: AddTeamMemberForm) => {
        try {
            await inviteNewTeamMember({
                ...data,
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                email: data.email.trim(),
            }).unwrap();
            toast.success("Team member invited successfully");
            navigate('/provider/team-members');
        } catch (error: any) {
            toast.error(getErrorMessage(error.data) ?? "There was an error");
        }
    };

    const setPermissionValue = (value: any[]) => {
        if (value) setValue('permissions', value, {shouldValidate: true});
    }
   
    return (
        <Fragment>
            <SelectedOptionTopBar title='Add New Team Member' onBackHandler={onBackFunction} SaveButtonText='Save Changes' isValid={isValid && !isInviting} onSaveHandler={handleSubmit(saveChanges)}/>
            <div className={styles.formSection}>
                <FormWrapper titleSection={titleSection}>
                    <div className={`formFieldInlineContainer ${styles.formInlineRow}`}>
                        <CommonFormFieldWrapper title="First name" required errors={getFormInputErrors(errors.firstName?.message)}>
                            <input {...register('firstName', { required: 'First name is required' })} placeholder="Enter First Name" />
                        </CommonFormFieldWrapper>
                        <CommonFormFieldWrapper title="Last name" required errors={getFormInputErrors(errors.lastName?.message)}>
                            <input {...register('lastName', { required: 'Last name is required' })} placeholder="Enter Last Name" />
                        </CommonFormFieldWrapper>
                    </div>
                    <div className={`formFieldInlineContainer`}>
                        <CommonFormFieldWrapper title="Email address" required errors={getFormInputErrors(errors.email?.message)}>
                            <input  
                            {...register('email', { required: 'Email is required', pattern: {
                                value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                                message: 'Invalid email address'
                            }})} 
                            placeholder="Enter Email" />
                        </CommonFormFieldWrapper>
                    </div>
                    <div className={`formFieldInlineContainer`}>
                        <CommonFormFieldWrapper title="Phone number" required={true} errors={getFormInputErrors(errors.phoneNumber?.message)}>
                            <CommonPhoneInput
                            registerInputProps={register('phoneNumber', {required: 'Phone Number is required' , pattern: {
                                value: /^\+(?:[0-9] ?){12}[0-9]$/,
                                message: 'Invalid phone number'
                            }})} 
                            value={watch('phoneNumber') ?? ''} 
                            defaultCountry='ng' 
                            onChange={(value) => setValue('phoneNumber', value, {
                                shouldDirty: true
                            })} />
                        </CommonFormFieldWrapper>
                    </div>
                    <div className={`formFieldInlineContainer`}>
                        <CommonFormFieldWrapper title="Role" required>
                                <CommonSelect 
                                registerProp={register('employeeType', { required: "Role is required" })} 
                                items={roles} 
                                selected={roles.find(x => x.label === watch('employeeType'))} 
                                placeholder='Select' 
                                searchable={true}
                                onChange={(value) => setValue('employeeType', value ? value.key : '', {shouldValidate: true, shouldDirty: true})} />
                        </CommonFormFieldWrapper>
                    </div>
                    <div className={`formFieldInlineContainer ${styles.formInlineRow} ${styles.columnFlow}`}>
                        <CommonCheckBoxList 
                        title='Modules' 
                        required
                        value={watch('permissions')}
                        registerProp={register('permissions', { required: 'Permission must be selected'})} 
                        checkboxTitleProperty='name' 
                        itemsList={permissions}
                        onBottomButtonClick={(value) => setPermissionValue(value)}/>
                    </div>
                </FormWrapper>
            </div>
            
        </Fragment>
    )
})

export default AddTeamMember